<script setup>
import { isNil } from 'lodash'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { BUTTON_SIZES_NEXT, BUTTON_VARIANTS } from '@/utils/components-configurations/app-button'
import { handleError } from '@/utils/error-handling'
import {
  AI_REPORT_DEBUG_EFFORT_LABEL_KEYS,
  AI_REPORT_DEBUG_KEYS,
  AI_REPORT_DEBUG_MODEL_KEYS,
  AI_REPORT_DEBUG_NUMBER_FIELDS,
  AI_REPORT_DEBUG_PROMPT_ANCHOR,
  AI_REPORT_DEBUG_PROMPT_KEY_BY_FIELD,
  AI_REPORT_DEBUG_SETTINGS_RESPONSE_KEYS
} from '@/utils/home-page/ai-report'
import { useAiReportDebugSettings } from '@/utils/home-page/ai-report-debug-settings'
import { showNotify } from '@/utils/notify'

import AppFieldError from '@/components/form/AppFieldError'
import FormFieldNext from '@/components/form/FormFieldNext'
import {
  HOMEPAGE_AI_REPORT_DEBUG_CLEAR_BUTTON_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_EFFORT_SELECT_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_ELEMENTS_PER_CALL_INPUT_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_MODEL_SELECT_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_PAIRS_PER_CALL_INPUT_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_SAVE_BUTTON_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_SETTINGS_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_SUMMARY_SYSTEM_PROMPT_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_SUMMARY_USER_PROMPT_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_SYSTEM_PROMPT_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_TIME_LIMIT_INPUT_TEST_ID,
  HOMEPAGE_AI_REPORT_DEBUG_USER_PROMPT_TEST_ID
} from '@/components/home-page/jest-helpers'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppInput from '@/components/ui/AppInput/AppInput'
import AppSelect from '@/components/ui/AppSelect/AppSelect'
import AppTextarea from '@/components/ui/AppTextarea/AppTextarea'

defineOptions({
  name: 'HomePageAiReportDebugSettings'
})

const props = defineProps({
  // the response of GET /homepage/aiReport/debug/settings: the models, the efforts, the
  // configured values and the text of the prompt files
  debugSettings: {
    type: Object,
    required: true
  }
})

const { t } = useI18n()

const {
  MODEL,
  EFFORT,
  SYSTEM_PROMPT,
  USER_PROMPT,
  SUMMARY_SYSTEM_PROMPT,
  SUMMARY_USER_PROMPT,
  TIME_LIMIT_SECONDS,
  PAIRS_PER_CALL,
  ELEMENTS_PER_CALL
} = AI_REPORT_DEBUG_KEYS

const { MODELS, EFFORTS, DEFAULTS, PROMPTS } = AI_REPORT_DEBUG_SETTINGS_RESPONSE_KEYS

const NUMBER_INPUTS = [
  {
    field: TIME_LIMIT_SECONDS,
    labelKey: 'homepage.ai_reports.debug.label.time_limit_seconds',
    testId: HOMEPAGE_AI_REPORT_DEBUG_TIME_LIMIT_INPUT_TEST_ID
  },
  {
    field: PAIRS_PER_CALL,
    labelKey: 'homepage.ai_reports.debug.label.pairs_per_call',
    testId: HOMEPAGE_AI_REPORT_DEBUG_PAIRS_PER_CALL_INPUT_TEST_ID
  },
  {
    field: ELEMENTS_PER_CALL,
    labelKey: 'homepage.ai_reports.debug.label.elements_per_call',
    testId: HOMEPAGE_AI_REPORT_DEBUG_ELEMENTS_PER_CALL_INPUT_TEST_ID
  }
]

const PROMPT_INPUTS = [
  {
    field: SYSTEM_PROMPT,
    labelKey: 'homepage.ai_reports.debug.label.system_prompt',
    testId: HOMEPAGE_AI_REPORT_DEBUG_SYSTEM_PROMPT_TEST_ID
  },
  {
    field: USER_PROMPT,
    labelKey: 'homepage.ai_reports.debug.label.user_prompt',
    testId: HOMEPAGE_AI_REPORT_DEBUG_USER_PROMPT_TEST_ID
  },
  {
    field: SUMMARY_SYSTEM_PROMPT,
    labelKey: 'homepage.ai_reports.debug.label.summary_system_prompt',
    testId: HOMEPAGE_AI_REPORT_DEBUG_SUMMARY_SYSTEM_PROMPT_TEST_ID
  },
  {
    field: SUMMARY_USER_PROMPT,
    labelKey: 'homepage.ai_reports.debug.label.summary_user_prompt',
    testId: HOMEPAGE_AI_REPORT_DEBUG_SUMMARY_USER_PROMPT_TEST_ID
  }
]

// the prompts are sent whole: the dialog sets no length limit of its own
const PROMPT_MAX_LENGTH = Infinity

const POSITIVE_WHOLE_NUMBER_PATTERN = /^[1-9]\d*$/

const { readSettings, saveSettings, clearSettings } = useAiReportDebugSettings()

const servedDefaults = computed(() => props.debugSettings[DEFAULTS] ?? {})

// the configured values and the text of the prompt files, as the form shows them (FR-0.13)
const servedValues = computed(() => {
  const prompts = props.debugSettings[PROMPTS] ?? {}

  const values = {
    [MODEL]: servedDefaults.value[MODEL] ?? null,
    [EFFORT]: servedDefaults.value[EFFORT] ?? null
  }

  AI_REPORT_DEBUG_NUMBER_FIELDS.forEach(field => {
    const value = servedDefaults.value[field]

    values[field] = isNil(value) ? '' : String(value)
  })

  Object.entries(AI_REPORT_DEBUG_PROMPT_KEY_BY_FIELD).forEach(([field, promptKey]) => {
    values[field] = prompts[promptKey] ?? ''
  })

  return values
})

// the saved settings of the person over the served values (FR-0.8)
const createFormValues = () => {
  const savedSettings = readSettings() ?? {}
  const values = { ...servedValues.value }

  Object.keys(values).forEach(field => {
    const savedValue = savedSettings[field]

    if (isNil(savedValue)) {
      return
    }

    values[field] = AI_REPORT_DEBUG_NUMBER_FIELDS.includes(field) ? String(savedValue) : savedValue
  })

  return values
}

// the dialog mounts the form anew on each read of the settings
const formValues = ref(createFormValues())

const modelOptions = computed(() => {
  return (props.debugSettings[MODELS] ?? []).map(model => {
    const name = model[AI_REPORT_DEBUG_MODEL_KEYS.NAME]

    // a model without a fixed price stays selectable (D-13)
    const label = model[AI_REPORT_DEBUG_MODEL_KEYS.PRICED]
      ? name
      : t('homepage.ai_reports.debug.model_option_no_price', {
          name,
          noPrice: t('homepage.ai_reports.debug.no_price')
        })

    return { label, value: model[AI_REPORT_DEBUG_MODEL_KEYS.ID] }
  })
})

const effortOptions = computed(() => {
  return (props.debugSettings[EFFORTS] ?? []).map(effort => {
    const labelKey = AI_REPORT_DEBUG_EFFORT_LABEL_KEYS[effort]

    return { label: labelKey ? t(labelKey) : effort, value: effort }
  })
})

const promptAnchorNote = computed(() => {
  return t('homepage.ai_reports.debug.prompt_anchor_note', {
    anchor: AI_REPORT_DEBUG_PROMPT_ANCHOR
  })
})

// the waiting time, the pairs per call and the Elements per call are positive whole numbers (D-9)
const invalidNumberFields = computed(() => {
  return AI_REPORT_DEBUG_NUMBER_FIELDS.filter(field => {
    return !POSITIVE_WHOLE_NUMBER_PATTERN.test(String(formValues.value[field]).trim())
  })
})

const isFormValid = computed(() => invalidNumberFields.value.length === 0)

const isNumberFieldInvalid = field => invalidNumberFields.value.includes(field)

// only the fields that differ from the served values are kept: the others take their
// configured value on the server, also after the configuration changes (FR-0.7)
const changedSettings = computed(() => {
  const settings = {}

  Object.entries(formValues.value).forEach(([field, value]) => {
    if (AI_REPORT_DEBUG_NUMBER_FIELDS.includes(field)) {
      const number = Number(String(value).trim())

      if (number !== servedDefaults.value[field]) {
        settings[field] = number
      }

      return
    }

    if (value !== servedValues.value[field]) {
      settings[field] = value
    }
  })

  return settings
})

const onSave = async () => {
  if (!isFormValid.value) {
    return
  }

  try {
    await saveSettings(changedSettings.value)
    showNotify({ title: t('homepage.ai_reports.debug.saved') })
  } catch (error) {
    handleError({ error })
  }
}

// every setting returns to its configured value and the prompts to the files' text (FR-0.13)
const onClear = () => {
  clearSettings()
  formValues.value = { ...servedValues.value }
  showNotify({ title: t('homepage.ai_reports.debug.cleared') })
}
</script>

<template>
  <div
    :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_SETTINGS_TEST_ID"
    :data-testid="HOMEPAGE_AI_REPORT_DEBUG_SETTINGS_TEST_ID"
    class="hards-Settings"
  >
    <div class="hards-Note">
      {{ t('homepage.ai_reports.debug.settings_note') }}
    </div>

    <div class="hards-Heading">
      {{ t('homepage.ai_reports.debug.section.settings') }}
    </div>

    <div class="hards-Fields">
      <FormFieldNext :label="t('homepage.ai_reports.debug.label.model')">
        <AppSelect
          v-model="formValues[MODEL]"
          :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_MODEL_SELECT_TEST_ID"
          :data-testid="HOMEPAGE_AI_REPORT_DEBUG_MODEL_SELECT_TEST_ID"
          :options="modelOptions"
        />
      </FormFieldNext>

      <FormFieldNext :label="t('homepage.ai_reports.debug.label.effort')">
        <AppSelect
          v-model="formValues[EFFORT]"
          :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_EFFORT_SELECT_TEST_ID"
          :data-testid="HOMEPAGE_AI_REPORT_DEBUG_EFFORT_SELECT_TEST_ID"
          :options="effortOptions"
        />
      </FormFieldNext>

      <FormFieldNext
        v-for="numberInput in NUMBER_INPUTS"
        :key="numberInput.field"
        :label="t(numberInput.labelKey)"
      >
        <AppInput
          v-model="formValues[numberInput.field]"
          :data-auto-testid="numberInput.testId"
          :data-testid="numberInput.testId"
          :is-error="isNumberFieldInvalid(numberInput.field)"
          inputmode="numeric"
          size="xlg"
        />
        <AppFieldError :show="isNumberFieldInvalid(numberInput.field)">
          {{ t('homepage.ai_reports.debug.positive_whole_number') }}
        </AppFieldError>
      </FormFieldNext>
    </div>

    <div class="hards-Heading">
      {{ t('homepage.ai_reports.debug.section.prompts') }}
    </div>

    <div class="hards-Note">
      {{ promptAnchorNote }}
    </div>

    <FormFieldNext
      v-for="promptInput in PROMPT_INPUTS"
      :key="promptInput.field"
      :label="t(promptInput.labelKey)"
    >
      <AppTextarea
        v-model="formValues[promptInput.field]"
        :data-auto-testid="promptInput.testId"
        :data-testid="promptInput.testId"
        :max-length="PROMPT_MAX_LENGTH"
      />
    </FormFieldNext>

    <div class="hards-Actions">
      <AppButtonNext
        :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_CLEAR_BUTTON_TEST_ID"
        :data-testid="HOMEPAGE_AI_REPORT_DEBUG_CLEAR_BUTTON_TEST_ID"
        :size="BUTTON_SIZES_NEXT.LG"
        :variant="BUTTON_VARIANTS.SECONDARY_GRAY"
        @click="onClear"
      >
        {{ t('homepage.ai_reports.debug.clear') }}
      </AppButtonNext>
      <AppButtonNext
        :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_SAVE_BUTTON_TEST_ID"
        :data-testid="HOMEPAGE_AI_REPORT_DEBUG_SAVE_BUTTON_TEST_ID"
        :disabled="!isFormValid"
        :size="BUTTON_SIZES_NEXT.LG"
        :variant="BUTTON_VARIANTS.PRIMARY"
        @click="onSave"
      >
        {{ t('homepage.ai_reports.debug.save') }}
      </AppButtonNext>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hards-Settings {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: $system-ui;
}

.hards-Heading {
  color: $dark-1;
  font-size: $fs-16;
  font-weight: fw('bold');
  line-height: 24px;
}

.hards-Note {
  color: $dark-3;
  font-size: $fs-14;
  line-height: 20px;
}

.hards-Fields {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.hards-Actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
